import { useRef, useState } from 'react'
import { Camera, ImagePlay, Images, ListChecks, PlusCircle, Sticker, Upload } from 'lucide-react'
import { ContextMenu, MenuItem } from '../ContextMenu'
import { Modal } from '../settings/Modal'
import { useIsMobile } from '@/lib/mobile'
import styles from './ComposerPlus.module.css'

interface Props {
  className: string | undefined
  disabled: boolean
  canAttach: boolean
  /** Можно ли создавать опросы (SEND_POLLS); нет — «+» сразу открывает выбор файла, как раньше. */
  canPoll: boolean
  onUpload: () => void
  onPoll: () => void
  onFiles: (files: FileList) => void
  onGif?: (() => void) | undefined
  onSticker: () => void
}

/** «+» слева от поля ввода: файл или опрос. Меню открывается вверх — поле ввода внизу окна. */
export function ComposerPlus({ className, disabled, canAttach, canPoll, onUpload, onPoll, onFiles, onGif, onSticker }: Props) {
  const button = useRef<HTMLButtonElement>(null)
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const [sheet, setSheet] = useState(false)
  const media = useRef<HTMLInputElement>(null)
  const camera = useRef<HTMLInputElement>(null)
  const mobile = useIsMobile()

  if (!mobile && !canPoll) {
    return (
      <button type="button" className={className} data-tooltip={canAttach ? 'Прикрепить файл' : 'Нет права прикреплять файлы'} aria-label="Прикрепить файл" disabled={disabled || !canAttach} onClick={onUpload}>
        <PlusCircle size={24} />
      </button>
    )
  }
  const open = () => {
    const r = button.current?.getBoundingClientRect()
    if (r) setMenu({ x: r.left, y: r.top - 96 })
  }
  return (
    <>
      <button ref={button} type="button" className={className} data-tooltip="Добавить" aria-label="Добавить файл или опрос" aria-haspopup={mobile ? 'dialog' : 'menu'} aria-expanded={Boolean(menu) || sheet} disabled={disabled} onClick={() => {
        if (mobile) { (document.activeElement as HTMLElement | null)?.blur(); setSheet(true) }
        else if (menu) setMenu(null)
        else open()
      }}>
        <PlusCircle size={24} />
      </button>
      <input ref={media} type="file" accept="image/*,video/*" multiple hidden disabled={disabled || !canAttach} onChange={(e) => { if (e.target.files) onFiles(e.target.files); e.target.value = '' }} />
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden disabled={disabled || !canAttach} onChange={(e) => { if (e.target.files) onFiles(e.target.files); e.target.value = '' }} />
      {sheet && mobile ? (
        <Modal title="Добавить" onClose={() => setSheet(false)}>
          <div className={styles.actions}>
            <button type="button" disabled={!canAttach} onClick={() => { media.current?.click(); setSheet(false) }}><Images size={22} />Фото или видео</button>
            <button type="button" disabled={!canAttach} onClick={() => { camera.current?.click(); setSheet(false) }}><Camera size={22} />Камера</button>
            <button type="button" disabled={!canAttach} onClick={() => { onUpload(); setSheet(false) }}><Upload size={22} />Файл</button>
            {onGif ? <button type="button" onClick={() => { setSheet(false); onGif() }}><ImagePlay size={22} />Гифки</button> : null}
            <button type="button" onClick={() => { setSheet(false); onSticker() }}><Sticker size={22} />Стикеры</button>
            {canPoll ? <button type="button" onClick={() => { setSheet(false); onPoll() }}><ListChecks size={22} />Создать опрос</button> : null}
          </div>
        </Modal>
      ) : null}
      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} label="Добавить" onClose={() => setMenu(null)}>
          <MenuItem icon={<Upload size={16} />} label="Загрузить файл" hint={canAttach ? undefined : 'нет права'} disabled={!canAttach} onClick={() => { setMenu(null); onUpload() }} />
          <MenuItem icon={<ListChecks size={16} />} label="Создать опрос" onClick={() => { setMenu(null); onPoll() }} />
        </ContextMenu>
      ) : null}
    </>
  )
}
